/**
 * Blog post carousel: edge fades, prev/next step scroll, pointer drag (desktop).
 * Card text block height is fixed in CSS from theme settings (no runtime measure).
 */
(function () {
  const SCROLL_EPS = 4;
  const DRAG_CLICK_CANCEL_PX = 8;

  function updateEdgeFades(viewport, track) {
    const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    const left = track.scrollLeft;
    viewport.classList.toggle('is-scrolled', left > SCROLL_EPS);
    const atEnd = maxScroll <= SCROLL_EPS || left >= maxScroll - SCROLL_EPS;
    viewport.classList.toggle('is-at-end', atEnd);
  }

  function getScrollStep(track) {
    const card = track.querySelector('[data-blog-carousel-card]');
    if (!card) {
      return Math.max(120, track.clientWidth * 0.5);
    }
    const style = window.getComputedStyle(track);
    const gap = parseFloat(style.gap || style.columnGap) || 0;
    const cardW = card.getBoundingClientRect().width;
    const desktop = window.matchMedia('(min-width: 750px)').matches;
    if (desktop) {
      const n = Math.max(1, Math.floor((track.clientWidth + gap) / (cardW + gap)));
      return n * (cardW + gap);
    }
    return cardW + gap;
  }

  function bindChrome(root, viewport, track) {
    const prev = root.querySelector('[data-blog-carousel-prev]');
    const next = root.querySelector('[data-blog-carousel-next]');

    function syncNav() {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const left = track.scrollLeft;
      if (prev) {
        prev.disabled = left <= SCROLL_EPS;
      }
      if (next) {
        next.disabled = left >= maxScroll - SCROLL_EPS;
      }
    }

    function onScroll() {
      updateEdgeFades(viewport, track);
      syncNav();
    }

    function onResize() {
      onScroll();
    }

    track.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize);

    let fadeSyncRaf = null;
    function scheduleFadeSync() {
      if (fadeSyncRaf != null) {
        return;
      }
      fadeSyncRaf = requestAnimationFrame(function () {
        fadeSyncRaf = null;
        onScroll();
      });
    }

    function bindFadeLayoutSync() {
      const imgs = track.querySelectorAll('img');
      for (let i = 0; i < imgs.length; i++) {
        const img = imgs[i];
        if (img.complete) {
          continue;
        }
        img.addEventListener('load', scheduleFadeSync, { once: true });
        img.addEventListener('error', scheduleFadeSync, { once: true });
      }
      window.addEventListener('load', scheduleFadeSync, { once: true });
      if (typeof ResizeObserver !== 'undefined') {
        const ro = new ResizeObserver(scheduleFadeSync);
        ro.observe(track);
      }
    }
    bindFadeLayoutSync();

    function step(direction) {
      track.scrollBy({
        left: getScrollStep(track) * direction,
        behavior: 'smooth',
      });
    }

    if (prev) {
      prev.addEventListener('click', function () {
        step(-1);
      });
    }
    if (next) {
      next.addEventListener('click', function () {
        step(1);
      });
    }

    onScroll();
    scheduleFadeSync();
    requestAnimationFrame(function () {
      onScroll();
    });
  }

  /**
   * Desktop drag scroll without setPointerCapture on the track — capture
   * steals clicks from the card <a>. Only scroll / preventDefault after drag
   * threshold so plain clicks open the post.
   */
  function bindDrag(track) {
    let activePointerId = null;
    let startX = 0;
    let startScroll = 0;
    let didDrag = false;

    function endDrag(e) {
      if (activePointerId !== e.pointerId) {
        return;
      }
      window.removeEventListener('pointermove', onWindowPointerMove, true);
      window.removeEventListener('pointerup', onWindowPointerUp, true);
      window.removeEventListener('pointercancel', onWindowPointerUp, true);

      const suppress = didDrag;
      track.classList.remove('is-dragging');
      activePointerId = null;

      if (suppress) {
        track.addEventListener(
          'click',
          function (ev) {
            ev.preventDefault();
            ev.stopPropagation();
          },
          { capture: true, once: true }
        );
      }
    }

    function onWindowPointerMove(e) {
      if (activePointerId !== e.pointerId) {
        return;
      }
      const dx = e.clientX - startX;
      if (Math.abs(dx) <= DRAG_CLICK_CANCEL_PX) {
        return;
      }
      didDrag = true;
      e.preventDefault();
      track.scrollLeft = startScroll - dx;
    }

    function onWindowPointerUp(e) {
      endDrag(e);
    }

    track.addEventListener('pointerdown', function (e) {
      if (e.pointerType === 'touch') {
        return;
      }
      if (e.button != null && e.button !== 0) {
        return;
      }
      activePointerId = e.pointerId;
      startX = e.clientX;
      startScroll = track.scrollLeft;
      didDrag = false;
      track.classList.add('is-dragging');
      window.addEventListener('pointermove', onWindowPointerMove, true);
      window.addEventListener('pointerup', onWindowPointerUp, true);
      window.addEventListener('pointercancel', onWindowPointerUp, true);
    });
  }

  function initRoot(root) {
    if (root.dataset.blogCarouselInitialized === 'true') {
      return;
    }
    const track = root.querySelector('[data-blog-carousel-track]');
    const viewport = root.querySelector('.custom-section--blog-post-carousel__viewport');
    if (!track || !viewport) {
      return;
    }

    root.dataset.blogCarouselInitialized = 'true';
    bindChrome(root, viewport, track);

    if (root.getAttribute('data-drag-enabled') === 'true') {
      bindDrag(track);
    }
  }

  function initAll(scope) {
    const roots = (scope || document).querySelectorAll('[data-blog-carousel]');
    for (let i = 0; i < roots.length; i++) {
      initRoot(roots[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    const target = event.target;
    if (target && target.nodeType === 1) {
      initAll(target);
    }
  });
})();
